import {
  Badge,
  Button,
  EmptyState,
  KeyValue,
  List,
  ListRow,
  MoreMenu,
  PageHeader,
  Section,
  Skeleton,
  Text,
  useToast,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { BackLink } from '../../../app/BackLink';
import { ConfirmDialog } from '../../../app/ConfirmDialog';
import { errorMessage } from '../../../lib/errors';
import { formatDate, formatDateTime } from '../../../lib/format';
import { useSession } from '../../identity/session';
import { isQueued } from '../../offline/offline';
import { useTask, useTaskAction, type Task } from '../api';
import { TaskFormDialog } from '../components/TaskFormDialog';
import styles from './TasksPages.module.css';

/** A task with its references, reminder and recurrence. */
export function TaskDetailPage() {
  const { t } = useTranslation();
  const { taskId = '' } = useParams();
  const task = useTask(taskId);
  if (task.error) {
    return (
      <EmptyState
        size="page"
        level={1}
        title={errorMessage(task.error)}
        actions={<BackLink to="/aufgaben" label={t('tasks.title')} />}
      />
    );
  }
  if (!task.data) {
    return (
      <div role="status" aria-label={t('common.loading')} className={styles.page}>
        <Skeleton width="40%" height="2rem" />
        <Skeleton lines={6} />
      </div>
    );
  }
  return <TaskDetail task={task.data} />;
}

export function useReminderText(task: Task): string | null {
  const { t } = useTranslation();
  const r = task.reminder;
  if (!r) return null;
  if (r.kind === 'at' && r.at) return t('tasks.detail.remindAt', { date: formatDateTime(r.at) });
  const time = r.time?.slice(0, 5) ?? '';
  if (r.days === 0) return t('tasks.detail.remindSameDay', { time });
  return t('tasks.detail.remindBefore', { count: r.days ?? 0, time });
}

function TaskDetail({ task }: { task: Task }) {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { can } = useSession();
  const [open, setOpen] = useState<'edit' | 'delete' | null>(null);
  const action = useTaskAction();
  const write = can('tasks.write');
  const done = task.status === 'done';
  const reminder = useReminderText(task);

  // Completing is reversible, so it happens at once with an undo (§6.10);
  // a recurring task has already created its next occurrence, so no undo.
  const toggle = () =>
    action.mutate(
      { id: task.id, action: done ? 'reopen' : 'complete' },
      {
        onSuccess: (result) => {
          // Captured offline: one toast, and no undo since nothing happened yet (#190).
          if (isQueued(result)) {
            toast.show(t('offline.captured'), 'info');
            return;
          }
          toast.show(
            done
              ? t('tasks.list.reopened')
              : task.recurrence
                ? t('tasks.list.completedNext')
                : t('tasks.list.completed'),
            'success',
            !done && !task.recurrence
              ? {
                  label: t('common.undo'),
                  onClick: () =>
                    action.mutate(
                      { id: task.id, action: 'reopen' },
                      { onError: (err) => toast.show(errorMessage(err), 'danger') },
                    ),
                }
              : undefined,
          );
        },
        onError: (err) => toast.show(errorMessage(err), 'danger'),
      },
    );

  const refs = [
    ...task.colonies.map((c) => ({
      key: c.id,
      href: `/voelker/${c.id}`,
      name: c.name,
      kind: t('tasks.detail.colony'),
    })),
    ...(task.location
      ? [
          {
            key: task.location.id,
            href: `/standorte/${task.location.id}`,
            name: task.location.name,
            kind: t('tasks.form.location'),
          },
        ]
      : []),
    ...(task.customer
      ? [
          {
            key: task.customer.id,
            href: `/kunden/${task.customer.id}`,
            name: task.customer.name,
            kind: t('tasks.form.customer'),
          },
        ]
      : []),
    ...(task.sale
      ? [
          {
            key: task.sale.id,
            href: `/verkauf/${task.sale.id}`,
            name: task.sale.name,
            kind: t('tasks.detail.sale'),
          },
        ]
      : []),
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        back={<BackLink to="/aufgaben" label={t('tasks.title')} />}
        title={task.title}
        meta={
          <>
            {task.due_on
              ? t('tasks.detail.due', { date: formatDate(task.due_on) })
              : t('tasks.detail.noDate')}
            {done ? (
              <>
                {' · '}
                <Badge tone="success">{t('tasks.status.done')}</Badge>
              </>
            ) : (
              task.overdue && (
                <>
                  {' · '}
                  <Badge tone="danger">{t('tasks.status.overdue')}</Badge>
                </>
              )
            )}
            {task.priority === 'high' && !done && (
              <>
                {' · '}
                <Badge tone="warning">{t('tasks.priority.highBadge')}</Badge>
              </>
            )}
          </>
        }
        actions={
          write && (
            <>
              <Button
                variant={done ? 'secondary' : 'primary'}
                loading={action.isPending}
                onClick={toggle}
              >
                {done ? t('tasks.detail.reopen') : t('tasks.detail.complete')}
              </Button>
              <MoreMenu
                label={t('common.moreActions')}
                items={[
                  { key: 'edit', label: t('common.edit'), onSelect: () => setOpen('edit') },
                  {
                    key: 'delete',
                    label: t('common.delete'),
                    tone: 'danger',
                    separated: true,
                    onSelect: () => setOpen('delete'),
                  },
                ]}
              />
            </>
          )
        }
      />

      <div className={styles.columns}>
        <div className={styles.stack}>
          <Section title={t('tasks.detail.details')}>
            <KeyValue
              items={[
                {
                  key: 'due',
                  label: t('tasks.form.dueOn'),
                  value:
                    task.due_on &&
                    `${formatDate(task.due_on)}${
                      task.due_time
                        ? `, ${task.due_time.slice(0, 5)} ${t('tasks.detail.oclock')}`
                        : ''
                    }`,
                },
                { key: 'reminder', label: t('tasks.form.reminder'), value: reminder },
                {
                  key: 'nextReminder',
                  label: t('tasks.detail.nextReminderLabel'),
                  value: task.remind_at && !done ? formatDateTime(task.remind_at) : null,
                },
                {
                  key: 'recurrence',
                  label: t('tasks.form.recurrence'),
                  value:
                    task.recurrence &&
                    (task.recurrence.every === 1
                      ? t(`tasks.recurrence.${task.recurrence.unit}`)
                      : t('tasks.list.every', {
                          count: task.recurrence.every,
                          unit: t(`tasks.unit.${task.recurrence.unit}`),
                        })),
                },
                {
                  key: 'assignee',
                  label: t('tasks.form.assignee'),
                  value: task.assignee?.name ?? t('tasks.form.nobody'),
                },
                {
                  key: 'created',
                  label: t('tasks.detail.createdBy'),
                  value: [task.created_by?.name, formatDateTime(task.created_at)]
                    .filter(Boolean)
                    .join(' · '),
                },
                {
                  key: 'done',
                  label: t('tasks.status.done'),
                  value:
                    done &&
                    [task.done_by, task.done_at ? formatDateTime(task.done_at) : null]
                      .filter(Boolean)
                      .join(' · '),
                },
              ]}
            />
          </Section>
          {task.description && (
            <Section title={t('tasks.form.description')} variant="content">
              <Text className={styles.notes}>{task.description}</Text>
            </Section>
          )}
        </div>
        {refs.length > 0 && (
          <Section title={t('tasks.form.refs')}>
            <List>
              {refs.map((r) => (
                <ListRow key={r.key} href={r.href} title={r.name} trailing={r.kind} />
              ))}
            </List>
          </Section>
        )}
      </div>

      {open === 'edit' && <TaskFormDialog open task={task} onClose={() => setOpen(null)} />}
      <ConfirmDialog
        open={open === 'delete'}
        onClose={() => {
          action.reset();
          setOpen(null);
        }}
        title={t('tasks.detail.deleteTitle', { title: task.title })}
        description={task.recurrence ? t('tasks.detail.deleteText') : undefined}
        confirmLabel={t('common.delete')}
        pending={action.isPending}
        error={action.error ? errorMessage(action.error) : undefined}
        onConfirm={() =>
          action.mutate(
            { id: task.id, action: 'delete' },
            {
              onSuccess: () => {
                toast.show(t('tasks.detail.deleted'));
                void navigate('/aufgaben');
              },
            },
          )
        }
      />
    </div>
  );
}
